import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { formatAsCLP } from "src/utils/formatNumber";
import Chart from "../charts/chart";
import useChart from "../charts/use-chart";
import {
  formatearPorcentaje,
  limitarPorcentajeGrafico,
  MontoResumen,
} from "./kpi-utils";

const ETIQUETAS_TIPO = {
  PLAN: "Planes",
  TAREA: "Tareas",
  AFECTA: "Afectas",
};

AppKpiMorosidad.propTypes = {
  data: PropTypes.shape({
    actual: PropTypes.object,
    anterior: PropTypes.object,
    periodoActual: PropTypes.object,
    periodoAnterior: PropTypes.object,
    porTipo: PropTypes.array,
    variacionPuntosPorcentuales: PropTypes.number,
  }),
  error: PropTypes.bool,
  loading: PropTypes.bool,
};

export default function AppKpiMorosidad({
  data,
  error = false,
  loading = false,
}) {
  const theme = useTheme();
  const actual = data?.actual || {};
  const morosidad = actual.morosidadPorcentaje;
  const variacion = data?.variacionPuntosPorcentuales;
  const porTipo = data?.porTipo || [];
  const antiguedad = actual.antiguedad || {};
  const fechaCorte = data?.periodoActual?.fechaCorte || "";
  const fechaCorteAnterior = data?.periodoAnterior?.fechaCorte || "";

  const chartOptions = useChart({
    chart: { sparkline: { enabled: true } },
    colors: [theme.palette.error.main],
    labels: ["Morosidad"],
    legend: { show: false },
    plotOptions: {
      radialBar: {
        hollow: { size: "66%" },
        track: { margin: 0 },
        dataLabels: {
          name: { offsetY: 24 },
          value: {
            offsetY: -16,
            fontSize: "28px",
            fontWeight: 700,
            formatter: () => formatearPorcentaje(morosidad),
          },
        },
      },
    },
    tooltip: { enabled: false },
  });

  if (loading) {
    return (
      <Card sx={{ minHeight: 410 }}>
        <CardHeader
          title={<Skeleton width={220} />}
          subheader={<Skeleton width={320} />}
        />
        <Grid container spacing={4} sx={{ px: 3, pb: 3 }}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="circular" width={220} height={220} />
          </Grid>
          {[1, 2].map((item) => (
            <Grid item xs={12} md={4} key={item}>
              <Stack spacing={2}>
                {[1, 2, 3].map((row) => (
                  <Skeleton key={row} height={48} />
                ))}
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ minHeight: 360 }}>
        <CardHeader
          title="Tasa de morosidad"
          subheader="Fotografía de cartera vencida / pendiente a la fecha de corte"
        />
        <Box sx={{ px: 3, pb: 3 }}>
          <Alert severity="error">
            No fue posible obtener el indicador. Intenta nuevamente.
          </Alert>
        </Box>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card sx={{ minHeight: 360 }}>
        <CardHeader
          title="Tasa de morosidad"
          subheader="Fotografía de cartera vencida / pendiente a la fecha de corte"
        />
        <Box sx={{ px: 3, pb: 3 }}>
          <Alert severity="info">
            No hay información disponible para los filtros seleccionados.
          </Alert>
        </Box>
      </Card>
    );
  }

  const colorVariacion =
    variacion < 0
      ? "success.main"
      : variacion > 0
        ? "error.main"
        : "text.secondary";
  const maximoTramo = Math.max(
    antiguedad.dias1a30 || 0,
    antiguedad.dias31a60 || 0,
    antiguedad.dias61a90 || 0,
    antiguedad.mas90Dias || 0,
    1,
  );
  const tramos = [
    { label: "1–30 días", value: antiguedad.dias1a30 || 0 },
    { label: "31–60 días", value: antiguedad.dias31a60 || 0 },
    { label: "61–90 días", value: antiguedad.dias61a90 || 0 },
    { label: "Más de 90 días", value: antiguedad.mas90Dias || 0 },
  ];

  return (
    <Card
      sx={{
        minHeight: 410,
        borderTop: `4px solid ${theme.palette.error.main}`,
      }}
    >
      <CardHeader
        title="Tasa de morosidad"
        subheader={`Fotografía de cartera vencida / pendiente al corte ${fechaCorte}`}
        sx={{ pb: 1 }}
      />

      <Grid container spacing={4} alignItems="center" sx={{ px: 3, pb: 3 }}>
        <Grid item xs={12} md={4}>
          <Stack alignItems="center">
            <Chart
              type="radialBar"
              series={[limitarPorcentajeGrafico(morosidad)]}
              options={chartOptions}
              width={220}
              height={220}
            />
            <Typography
              variant="body2"
              sx={{ color: colorVariacion, textAlign: "center" }}
            >
              {variacion === null || variacion === undefined
                ? "Sin base comparable en el corte anterior"
                : `${variacion > 0 ? "+" : ""}${variacion.toLocaleString(
                    "es-CL",
                  )} pp respecto del corte ${fechaCorteAnterior}`}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Stack
            divider={<Divider flexItem sx={{ borderStyle: "dashed" }} />}
            spacing={2.25}
          >
            <MontoResumen
              label="Cartera pendiente"
              value={actual.carteraPendiente}
              secondary={`${actual.cantidadFacturasPendientes || 0} facturas`}
            />
            <MontoResumen
              label="Cartera vencida"
              value={actual.carteraVencida}
              secondary={`${actual.cantidadFacturasVencidas || 0} facturas`}
            />
            <MontoResumen
              label="Cartera vigente"
              value={actual.carteraVigente}
            />
            <MontoResumen
              label="Incobrable excluida"
              value={actual.incobrableExcluida}
              secondary={`${
                actual.cantidadFacturasIncobrables || 0
              } facturas · no afecta la tasa`}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Antigüedad de la cartera vencida
          </Typography>
          <Stack spacing={1.75}>
            {tramos.map((tramo) => (
              <Box key={tramo.label}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{tramo.label}</Typography>
                  <Typography variant="subtitle2">
                    {formatAsCLP(tramo.value)}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(tramo.value / maximoTramo) * 100}
                  color="error"
                  sx={{ mt: 0.75, height: 6, borderRadius: 1 }}
                />
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>

      <Divider />

      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Desglose por origen
        </Typography>
        <Grid container spacing={2}>
          {porTipo.map((item) => (
            <Grid item xs={12} sm={4} key={item.tipo}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: "background.neutral",
                }}
              >
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">
                    {ETIQUETAS_TIPO[item.tipo] || item.tipo}
                  </Typography>
                  <Typography variant="subtitle2">
                    {formatearPorcentaje(item.actual.morosidadPorcentaje)}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {formatAsCLP(item.actual.carteraVencida)} vencido de{" "}
                  {formatAsCLP(item.actual.carteraPendiente)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Card>
  );
}
